import React, { createContext, lazy, Suspense, PropsWithChildren } from 'react';
import { LicenseManager } from '@ag-grid-enterprise/all-modules';
import { ResizeSensor } from '@blueprintjs/core';
import { VDraggableModalProvider } from 'welltrax-ui-components';
import { Observer } from 'mobx-react';
import moment from 'moment';
import { InjectedIntl, injectIntl, IntlProvider } from 'react-intl';
import { RiftGate, RiftProvider } from 'rift-router';

import './App.scss';
import './core/services/ZohoHCAsap';

import LoaderApp from './core/components/commons/LoaderApp/LoaderApp';
import LoaderAppMin from './core/components/commons/LoaderApp/LoaderAppMin';
import appStore, { AppStore, ITheme, themes } from './core/stores/AppStore';
import store from './core/stores/RootStore';
import routeStore from './core/stores/RouterStore';

import { ErrorBoundary } from './core/components/commons/ErrorBoundary/ErrorBoundary';
import { setIntl } from './core/utils/Intl';
import { retryImport } from './core/utils/RetryImport';
import { SocketConnection } from './modules/commons/utils/SocketConnection';
import { flattenMessages } from './modules/commons/utils/utils';

const AppOverlay = lazy(() => retryImport(() => import('./modules/commons/components/appOverlay/AppOverlay')));

const licence = process.env.REACT_APP_AGGRID_LICENCE ? process.env.REACT_APP_AGGRID_LICENCE : '';
LicenseManager.setLicenseKey(licence);

export interface AppContextInterface {
  app: AppStore;
}

export interface ISocketAppContext {
  socket: SocketConnection;
}
export const SocketAppContext = React.createContext<ISocketAppContext>({
  socket: new SocketConnection()
});

const ResizeSensorProvider: PropsWithChildren<any> = ResizeSensor;
const ConfigIntlProvider: PropsWithChildren<any> = IntlProvider;
export const AppThemeContext = createContext<ITheme>(themes.light);
export const StoreAppContext = createContext<AppContextInterface>(store);
export const IntlContext = createContext<InjectedIntl>({} as InjectedIntl);
export const InjectIntlContext = injectIntl(({ intl, children }: PropsWithChildren<{ intl: any }>) => {
  setIntl(intl.formatMessage);
  return <IntlContext.Provider value={intl}>{children}</IntlContext.Provider>;
});

moment.locale(appStore.momentLocale);

const App = () => (
  <ErrorBoundary>
    <VDraggableModalProvider>
      <ResizeSensorProvider onResize={appStore.handleResize}>
        <div id={'App'} className="App">
          <Suspense fallback={process.env.REACT_APP_DS === 'DEPLOY' ? <LoaderAppMin /> : <LoaderApp />}>
            <Observer
              render={() => (
                <StoreAppContext.Provider value={store}>
                  <AppThemeContext.Provider value={appStore.theme}>
                    <ConfigIntlProvider
                      locale={appStore.lang}
                      key={appStore.lang}
                      messages={flattenMessages(appStore.messages)}
                    >
                      <InjectIntlContext>
                        <RiftProvider routes={routeStore}>
                          <RiftGate />
                        </RiftProvider>
                      </InjectIntlContext>
                    </ConfigIntlProvider>
                  </AppThemeContext.Provider>
                </StoreAppContext.Provider>
              )}
            />
            <AppOverlay />
          </Suspense>
        </div>
      </ResizeSensorProvider>
    </VDraggableModalProvider>
  </ErrorBoundary>
);

export default App;
